import { Box } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

export enum SunBurstIconType {
	PATIENT,
	NONE,
}

export enum SunBurstDataType {
	CURRENCY,
	ABSOLUTE,
}

interface SegmentDatum {
	id: number;
	referencedBy: Array<number>;
	count: number;
	percentage: number;
	isPlaceholder?: boolean;
	label: string;
	fill: string;
}

export interface SunBurstProps {
	data: Array<Array<SegmentDatum>>;
	isAnimationActive: boolean;
	totalPatientNumber: number;
	dataType: SunBurstDataType;
	iconType: SunBurstIconType;
	children?: any;
}

const LyfeSunBurst = (props: SunBurstProps) => {
	const [activeId, setActiveId] = useState<number | null>(null);
	const deepBlue = "#5e6e7d";
	const startInnerRadius = 60;
	const barWidth = 30;
	const [animation, setAnimation] = useState(props.isAnimationActive);

	const renderLabel =
		(totalPatientNumber: number) =>
		(props: any): SVGElement => {
			const RADIAN = Math.PI / 180;
			const { cx, cy, midAngle, outerRadius, fill, percent, value, startAngle, endAngle, id, payload } = props;

			if (payload.isPlaceholder) return (<g></g>) as any;

			const sin = Math.sin(-RADIAN * midAngle);
			const cos = Math.cos(-RADIAN * midAngle);
			const sx = cx + outerRadius * cos;
			const sy = cy + outerRadius * sin;
			const mx = cx + (outerRadius + 40) * cos;
			const my = cy + (outerRadius + 50) * sin;
			const ex = mx + (cos >= 0 ? 1 : -1) * 22;
			const ey = my;
			const textAnchor = cos >= 0 ? "start" : "end";
			const textVertShift = (startAngle === 360 || endAngle === 360) && endAngle - startAngle < 25 ? -36 : 0;

			return (
				<g>
					{/* Icon in the center of the pie */}
					<text x={cx} y={cy + 20} textAnchor="middle" fontSize="20" fill={deepBlue}>
						{totalPatientNumber}
					</text>
					<PersonIcon x={cx - 16} y={-20} htmlColor={deepBlue} width={34} />

					{id === activeId && (
						<>
							<text
								x={ex + (cos >= 0 ? 1 : -1) * 12}
								y={ey}
								dy={textVertShift}
								textAnchor={textAnchor}
								fontSize="18"
								fill={fill}
							>
								{Math.round(percent * totalPatientNumber)}
							</text>
							<PersonIcon x={ex + (cos >= 0 ? 50 : -70)} y={ey - 257} htmlColor={fill} width={20} />
							<text
								x={ex + (cos >= 0 ? 1 : -1) * 12}
								y={ey + 20}
								dy={textVertShift}
								textAnchor={textAnchor}
								fontSize="12px"
								fill={"#969392"}
							>
								{`(${Math.round(percent * 100)}%)`}
							</text>
							<path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" z={100} />
						</>
					)}
				</g>
			) as any;
		};

	const isSegmentHighlighted = (segment: SegmentDatum): boolean => {
		if (!segment) return false;

		const allSegments = props.data.flatMap((ring) => ring.map((segment) => segment));
		if (segment.id === activeId) return true;
		if (activeId && segment.referencedBy.includes(activeId)) return true;
		if (segment.referencedBy.length === 0) return false;

		return segment.referencedBy.some((referenced) => isSegmentHighlighted(allSegments.find((segment) => segment.id === referenced)!));
	};

	return (
		<Box minWidth="1136px" display="flex" style={{ justifyContent: "space-around", alignItems: "center", verticalAlign: "center" }}>
			{props.children}
			<Box width={500} height={500}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart width={400} height={400} style={{ top: "52px" }}>
						{props.data.map((ringData, index) => (
							<Pie
								label={renderLabel(props.totalPatientNumber)}
								labelLine={false}
								activeIndex={0}
								data={ringData}
								isAnimationActive={animation}
								onAnimationEnd={() => setAnimation(false)}
								cx="50%"
								cy="50%"
								innerRadius={startInnerRadius + index * barWidth}
								outerRadius={startInnerRadius + index * barWidth + barWidth}
								dataKey="percentage"
								key={index}
								onMouseEnter={(event, _) => !event.payload.isPlaceholder && setActiveId(event.payload.id)}
								onMouseLeave={() => setActiveId(null)}
							>
								{ringData.map((ringDatum, ringIndex) => (
									<Cell
										key={`cell-${index}-${ringIndex}`}
										fillOpacity={activeId !== null ? (isSegmentHighlighted(ringDatum) ? 1 : 0.3) : 1}
									/>
								))}
							</Pie>
						))}
					</PieChart>
				</ResponsiveContainer>
			</Box>
		</Box>
	);
};

export default LyfeSunBurst;
